import { Link, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import Input from "../../components/input";
import Button from "../../components/button";
import { toast } from "sonner";
import useRequest from "../../components/hook/use-req";
import { ClockLoader } from "react-spinners";
import { ALLOWED_COUNTRIES, countries } from "./data";
import AuthLayout from "../../components/layouts/auth-layout";
import SideImage from "../../assets/images/sideImage.png";
import Select from "react-select";
import { CountryOption, countryOptions, CustomPhoneInput, FormData } from "../../components/custom-phone-input";

// Types


const CreateProfile: React.FC = () => {
  const storedData = localStorage.getItem("userDetails");
  const data = storedData ? JSON.parse(storedData) : null;
  const email = data?.email;
  const token = localStorage.getItem("authToken");

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors }
  } = useForm<FormData>({
    defaultValues: {
      org_name: "",
      country_code: "ng",
      domain_name: "",
      phone_number: "",
    },
  });

  // Watch for country code changes
  const selectedCountryCode = watch("country_code");

  const { loading, makeRequest } = useRequest("/auth/postsignup", "POST", {
    Authorization: `Bearer ${token}`,
  });
  const navigate = useNavigate();

  // Filter countries to only show Nigeria and India
  const filteredCountries = countries.filter(
    (country: any) => country.code === "ng" || country.code === "in"
  );

  const handleCreateProfile = handleSubmit(async (formData: FormData) => {
    const selectedCountry = filteredCountries.find(
      (country: any) => country.code === formData.country_code
    );

    const phoneNumber = formData.phone_number;
    const countryCode = formData.country_code;

    if (!phoneNumber) {
      toast.error("Phone number is required");
      return;
    }

    const countryInfo = ALLOWED_COUNTRIES[countryCode];

    // Validate phone number
    if (!countryInfo.pattern.test(phoneNumber)) {
      toast.error(`Please enter a valid ${countryInfo.name} phone number (${countryInfo.format})`);
      return;
    }

    // Combine country code with phone number
    const fullPhoneNumber = `${countryInfo.dialCode}${phoneNumber}`;

    const user = {
      country_name: selectedCountry?.value || "",
      org_name: formData.org_name,
      phone_number: fullPhoneNumber,
      domain_name: formData.domain_name,
      email: email,
    };

    const [response] = await makeRequest(user);
    if (response.status) {
      toast.success(response.message || "Created successfully");
      navigate("/login");
    } else {
      toast.error(response.message || "Failed to create profile");
    }
  });

  return (
    <AuthLayout sideImageSrc={SideImage}>
      <div className="mb-32">
        <h1 className="text-[32px] font-bold text-[#3A3428]">
          Welcome to <span className="font-normal">ConvoHub</span>
        </h1>
        <p className="text-[16px] text-[#434343]">
          Reach customers on every channel, effortlessly.
        </p>
      </div>

      <form onSubmit={handleCreateProfile} className="w-full">
        <div className="flex gap-3">
          <Controller
            name="org_name"
            control={control}
            rules={{ required: "Organization name is required" }}
            render={({ field, fieldState }) => (
              <Input
                label="Organization Name"
                value={field.value}
                placeholder="Enter your organization name"
                className="w-full"
                error={fieldState?.error?.message}
                onChange={field.onChange}
                autoComplete="off"
              />
            )}
          />

          <Controller
            name="country_code"
            control={control}
            rules={{ required: "Country is required" }}
            render={({ field, fieldState }) => (
              <div className="w-full">
                <label className="block text-sm font-medium mb-1">Country</label>
                <Select<CountryOption>
                  options={countryOptions}
                  value={countryOptions.find(opt => opt.value === field.value)}
                  onChange={(option) => {
                    if (option) {
                      field.onChange(option.value);
                      setValue("phone_number", ""); // Clear phone when country changes
                    }
                  }}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      height: "45px",

                    }),
                  }}
                  className="w-full"
                  isSearchable={false}
                />
                {fieldState.error && (
                  <p className="text-red-500 text-sm mt-1">{fieldState.error.message}</p>
                )}
              </div>
            )}
          />
        </div>

        <div className="flex gap-3">
          <Controller
            name="domain_name"
            control={control}
            rules={{
              required: "Website URL is required",
              validate: {
                mustStartWithWWW: (value) =>
                  value.startsWith("www.") || "Website must start with www",
                noHttpOrHttps: (value) =>
                  !/^https?:\/\//.test(value) || "Do not include http:// or https://",
                validFormat: (value) => {
                  const pattern = /^www\.[\w-]+\.[\w-]{2,}$/;
                  return pattern.test(value) || "Enter a valid domain like: www.example.com";
                },
                noTrailingSlash: (value) =>
                  !value.endsWith("/") || "Website URL must not end with a slash",
              },
            }}
            render={({ field, fieldState }) => (
              <Input
                label="Website"
                value={field.value}
                placeholder="www.example.com"
                className="w-full mt-2"
                error={fieldState?.error?.message}
                onChange={field.onChange}
                autoComplete="off"
              />
            )}
          />

          <Controller
            name="phone_number"
            control={control}
            rules={{
              required: "Phone number is required",
              validate: (value) => {
                const countryInfo = ALLOWED_COUNTRIES[selectedCountryCode];
                if (!countryInfo.pattern.test(value)) {
                  return `Please enter a valid ${countryInfo.name} number (${countryInfo.format})`;
                }
                return true;
              },
            }}
            render={({ field, fieldState }) => (
              <div className="w-full mt-2">
                <label className="text-[12px] font-medium text-[#434343]">
                  Phone Number
                </label>

                <CustomPhoneInput
                  value={field.value || ""}
                  onChange={field.onChange}
                  countryCode={selectedCountryCode}
                  error={fieldState.error}
                />
                {fieldState.error && (
                  <p className="text-red-500 text-sm mt-1">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />
        </div>

        <Button
          size="lg"
          variant="primary"
          className="w-full mt-5"
          type="submit"
          disabled={loading}
        >
          {loading ? (
            <ClockLoader color="#ffffff" loading={loading} size={20} />
          ) : (
            "Complete Profile"
          )}
        </Button>
      </form>

      <p className="text-[#4F46E5] text-center text-[14px] my-4">
        <Link to="/login">Already have an account? Log in</Link>
      </p>
    </AuthLayout>
  );
};

export default CreateProfile;